import { Box, Text } from '@bechtel/gds';
import { find, get, map } from 'lodash';
import React, { FC, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormMode, MultiLingualText, useLanguage } from '~/components';
import { useSearchMetaDataTables } from '~/hooks';
import {
  ContainerBoxStyled,
  NavLinkStyled,
  StatusIndicatorAreaStyled,
  StatusIndicatorStyled,
  TextStyled,
} from './styled';
import { SectionListProps, SidebarEnum } from './types';

export const SectionList: FC<SectionListProps> = ({ items, mode, hideStatus, permissionCheck }) => {
  const { state } = useLocation();
  const [searchMetadataTables, { data }] = useSearchMetaDataTables();
  const { isAlternateLanguageUsed } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    searchMetadataTables({
      variables: {
        searchInput: {
          filterInput: {},
          sortInput: [
            {
              field: 'editList',
              sort: 'ASC',
            },
          ],
        },
      },
    });
  }, []);

  const metadataTables = get(data, 'searchMetadataTables.items', []);

  const getSectionItemLabel = (memberName: string) => {
    const metadataTable = find(metadataTables, (item) => item.memberName === memberName);
    return metadataTable
      ? isAlternateLanguageUsed
        ? metadataTable.alternateDescription
        : metadataTable.description
      : '';
  };

  const scrollToSection = (id, path) => {
    navigate(path);
    setTimeout(() => {
      const section = document.getElementById(id);
      // Searches for the element with the relevant id
      if (section) {
        // scrolls that element into the view
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }, 0);
  };

  return (
    <Box mt={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      {items.length > 0 &&
        map(items, (item) => {
          const { title, children, path, status, hidden, id } = item;
          const applyPermissions =
            ((state?.mode === FormMode.CLONE || mode === FormMode.CREATE) && path !== 'details') ||
            (path === 'barriers' && permissionCheck) ||
            false;
          if (!hidden) {
            return (
              <React.Fragment key={title}>
                <NavLinkStyled
                  to={path}
                  disabled={
                    state?.mode === FormMode.CLONE ||
                    mode === FormMode.CREATE ||
                    (path === 'barriers' && permissionCheck)
                  }
                >
                  {({ isActive }) => (
                    <ContainerBoxStyled
                      selectedItem={isActive}
                      mb={{ sm: '500' }}
                      onClick={() => scrollToSection(id, path)}
                    >
                      <Box
                        flexDirection={{ sm: 'column' }}
                        alignItems={{ sm: 'center' }}
                        pr={{ sm: status === SidebarEnum.FILLED ? '450' : '500' }}
                        pl={{ sm: '500' }}
                        py={{ sm: '000' }}
                      >
                        <Box flexDirection={{ sm: 'column' }} key={item.title}>
                          <Box flexDirection={{ sm: 'row' }} pt={{ sm: '300' }} pb={{ sm: '300' }}>
                            {item.displayMemberName ? (
                              <TextStyled size={400} color="primary.800" applyPermissions={applyPermissions}>
                                {getSectionItemLabel(item.displayMemberName)}
                              </TextStyled>
                            ) : (
                              <MultiLingualText
                                size={400}
                                color="primary.800"
                                style={{ opacity: applyPermissions ? '0.4' : '' }}
                              >
                                {title}
                              </MultiLingualText>
                            )}
                            {!hideStatus && (
                              <StatusIndicatorAreaStyled mr={{ sm: '500' }}>
                                <StatusIndicatorStyled
                                  selectedItem={status === SidebarEnum.NOT_FILLED}
                                ></StatusIndicatorStyled>
                              </StatusIndicatorAreaStyled>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </ContainerBoxStyled>
                  )}
                </NavLinkStyled>
                {children &&
                  map(children, (data) => {
                    if (!data.hidden) {
                      return (
                        <Box
                          key={data.title}
                          flexDirection={{ sm: 'row' }}
                          pl={{ sm: '600' }}
                          mr={{ sm: '500' }}
                          pb={{ sm: '500' }}
                          style={{ position: 'relative', cursor: 'pointer' }}
                          onClick={() => scrollToSection(data.id, path)}
                        >
                          {data.displayMemberName ? (
                            <Text
                              size={300}
                              color="primary.800"
                              style={{
                                opacity:
                                  (state?.mode === FormMode.CLONE || mode === FormMode.CREATE) && path !== 'details'
                                    ? '0.4'
                                    : '',
                              }}
                            >
                              {getSectionItemLabel(data.displayMemberName)}
                            </Text>
                          ) : (
                            <MultiLingualText size={300} color="primary.800">
                              {data.title}
                            </MultiLingualText>
                          )}

                          {!hideStatus && (
                            <StatusIndicatorAreaStyled>
                              <StatusIndicatorStyled
                                selectedItem={data.status === SidebarEnum.NOT_FILLED}
                                icon={'circle'}
                                iconStyle={data.status === SidebarEnum.NOT_FILLED ? 'regular' : 'solid'}
                              />
                            </StatusIndicatorAreaStyled>
                          )}
                        </Box>
                      );
                    }
                  })}
              </React.Fragment>
            );
          }
        })}
    </Box>
  );
};
